import React from 'react';
import { useLang } from '../../context/LangContext';
import { parts } from '../../content/learning';
import { getLocalized } from '../../content/learning/getLocalized';

const NAVY  = '#0d1b3e';
const GOLD  = '#c9a23a';
const MUTED = '#7a6040';
const TEXT  = '#2c2c2c';
const WARM_BG = '#f5e9d0';

export default function ChapterView({ chapter, completedItems, onToggle }) {
  const { t, lang } = useLang();
  if (!chapter) return null;

  const gl = (field) => getLocalized(field, lang);

  const itemId = (idx) => `${chapter.id}-ex-${idx}`;
  const isDone = (id) => completedItems.has(id);
  const partTitle = gl(parts.find((p) => p.num === chapter.part)?.title);

  return (
    <article>
      {/* Part breadcrumb */}
      {partTitle && (
        <div style={{ fontSize: 11, color: GOLD, fontWeight: 700, marginBottom: 14, letterSpacing: 0.5 }}>
          {t.learning?.part} {chapter.part} — {partTitle}
        </div>
      )}

      {/* Chapter header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 26 }}>
        <div style={{
          width: 50, height: 50, borderRadius: '50%',
          background: NAVY, color: GOLD,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 15, fontWeight: 800, flexShrink: 0, letterSpacing: 1,
        }}>
          {String(chapter.num).padStart(2, '0')}
        </div>
        <div style={{ paddingTop: 4 }}>
          <h2 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: NAVY, lineHeight: 1.3 }}>
            {gl(chapter.title)}
          </h2>
          {lang !== 'en' && chapter.title?.en && (
            <p style={{ margin: '5px 0 0', fontSize: 13, color: MUTED, direction: 'ltr', textAlign: 'start', fontStyle: 'italic' }}>
              {chapter.title.en}
            </p>
          )}
        </div>
      </div>

      {/* Intro */}
      {chapter.intro && (
        <div style={{
          background: WARM_BG,
          borderRadius: 8,
          padding: '15px 20px',
          marginBottom: 28,
          borderInlineStart: `4px solid ${GOLD}`,
        }}>
          <p style={{ margin: 0, color: TEXT, lineHeight: 2, fontSize: 15 }}>
            {gl(chapter.intro)}
          </p>
        </div>
      )}

      {/* Sections */}
      {chapter.sections?.map((sec, si) => (
        <div key={si} style={{ marginBottom: 26 }}>
          <h3 style={{
            margin: '0 0 12px', fontSize: 17, fontWeight: 700, color: NAVY,
            display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 8,
          }}>
            {gl(sec.heading)}
            {lang !== 'en' && sec.heading?.en && (
              <span style={{ fontSize: 11, color: MUTED, fontWeight: 400, direction: 'ltr', fontStyle: 'italic' }}>
                ({sec.heading.en})
              </span>
            )}
          </h3>
          {sec.body?.map((p, pi) => (
            <p key={pi} style={{ color: TEXT, lineHeight: 1.95, marginBottom: 10, fontSize: 14.5 }}>
              {gl(p)}
            </p>
          ))}
          {sec.bullets?.length > 0 && (
            <ul style={{ margin: '8px 0', paddingInlineStart: 24 }}>
              {sec.bullets.map((b, bi) => (
                <li key={bi} style={{ color: TEXT, lineHeight: 1.9, marginBottom: 8, fontSize: 14.5 }}>
                  {gl(b)}
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}

      {/* Taxonomy table — ch06 only */}
      {chapter.taxonomyTable?.length > 0 && (
        <div style={{ marginBottom: 28 }}>
          <h3 style={{ margin: '0 0 14px', fontSize: 17, fontWeight: 700, color: NAVY }}>
            {t.learning?.taxonomyHeading}
          </h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ background: 'rgba(201,162,58,0.1)' }}>
                  {[gl({ ar: 'النوع', en: 'Type', fa: 'نوع' }), 'English', gl({ ar: 'الوصف', en: 'Description', fa: 'توضیح' })].map((h, i) => (
                    <th key={i} style={{
                      padding: '10px 14px', color: NAVY, fontWeight: 700,
                      borderBottom: `2px solid ${GOLD}`,
                      textAlign: i === 1 ? 'left' : 'start',
                      direction: i === 1 ? 'ltr' : undefined,
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {chapter.taxonomyTable.map((row, ri) => (
                  <tr key={ri} style={{ borderBottom: '1px solid rgba(201,162,58,0.18)', background: ri % 2 ? 'rgba(201,162,58,0.04)' : 'transparent' }}>
                    <td style={{ padding: '9px 14px', fontWeight: 600, color: NAVY, whiteSpace: 'nowrap' }}>
                      {gl(row)}
                    </td>
                    <td style={{ padding: '9px 14px', color: MUTED, direction: 'ltr', textAlign: 'left', whiteSpace: 'nowrap' }}>
                      {row.en}
                    </td>
                    <td style={{ padding: '9px 14px', color: TEXT, lineHeight: 1.7 }}>
                      {gl(row.description)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Example */}
      {chapter.example && (
        <div style={{
          background: WARM_BG,
          border: `1px solid rgba(201,162,58,0.3)`,
          borderRadius: 8,
          padding: '14px 20px',
          marginBottom: 28,
        }}>
          <div style={{ fontSize: 12, color: GOLD, fontWeight: 700, marginBottom: 8 }}>
            {t.learning?.example}
          </div>
          <p style={{ margin: 0, color: TEXT, lineHeight: 1.95, fontSize: 14.5 }}>
            {gl(chapter.example)}
          </p>
        </div>
      )}

      {/* Exercises */}
      {chapter.exercise?.length > 0 && (
        <div style={{
          background: 'rgba(13,27,62,0.04)',
          border: '1px solid rgba(13,27,62,0.12)',
          borderRadius: 8,
          padding: '16px 20px',
        }}>
          <div style={{ fontSize: 13, color: NAVY, fontWeight: 700, marginBottom: 14 }}>
            {t.learning?.exercises} — {chapter.exercise.length}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
            {chapter.exercise.map((ex, ei) => {
              const id   = itemId(ei);
              const done = isDone(id);
              return (
                <label key={ei} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={done}
                    onChange={() => onToggle(id, !done)}
                    style={{ marginTop: 3, accentColor: GOLD, width: 16, height: 16, flexShrink: 0, cursor: 'pointer' }}
                  />
                  <span style={{
                    color: done ? '#a08060' : TEXT,
                    lineHeight: 1.9, fontSize: 14.5,
                    textDecoration: done ? 'line-through' : 'none',
                    transition: 'color 0.2s',
                  }}>
                    {gl(ex)}
                  </span>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </article>
  );
}
